/**
 * ── WHICH HOMES LEAD WHEN THERE ARE MORE THAN TEN (Jack, 2026-08-21) ───────────
 *
 * "always start with the move-in ready homes and then under construction and etc"
 * — his own Homesite Spec Sheet's card ordering, adopted verbatim (its comment:
 * "Status leads … and WITHIN each status the homes that actually render a photo
 * come first — so a move-in-ready home with no picture still sorts above an
 * under-construction one").
 *
 * Applied ONCE, where the pack enters the app (api.getCommunityPack), so the
 * Dynamic fill, the wizard picker, the counts, and Add by ID all read the SAME
 * order — two renderings of the same inventory must not disagree about which ten
 * homes exist (standing rule). Pick keys are identity-based (homeKey), never
 * index-based, so re-ordering cannot re-point an existing pick.
 *
 * The sort is STABLE: within status+photo, the feed's own order stands.
 * NOTHING here writes a status word into the email — ordering is invisible.
 */
import type { HomesiteInfo } from "../types/api";

/* the branded "images coming soon" family — same expression as echDyn/echRequired */
const PLACEHOLDER_RE = /comingsoon|coming-soon|placeholder|no-?image|\/default\//i;

/** Spec Sheet statusRank, verbatim: move-in ready 0 · under construction 1 ·
 *  any other status 2 · no status at all 3 */
export function homeStatusRank(status: unknown): number {
  const s = String(status || "").toUpperCase().replace(/[\s-]+/g, "_");
  if (!s) return 3;
  if (s === "MOVE_IN_READY") return 0;
  if (s === "UNDER_CONSTRUCTION") return 1;
  return 2;
}

/** a photo that will actually render beats none — placeholder art counts as none */
const photoRank = (img: unknown): number =>
  img && !PLACEHOLDER_RE.test(String(img)) ? 0 : 1;

/** the Listing Table's OWN rule: "Every home row that remains must have all four
 *  cells filled" — a home the feed can fully describe (specs, price, link) serves
 *  before one that would ship a row with an empty cell */
const completeRank = (r: HomesiteInfo): number =>
  r.sqft != null && r.sqft !== "" && r.beds != null && r.beds !== "" && r.baths != null && r.baths !== ""
  && !!r.price && !!r.url ? 0 : 1;

/**
 * ── WITHIN MOVE-IN READY, THE OLDEST GOES FIRST (Jack, 2026-08-22) ─────────────
 * "show the oldest move in ready homes first" — a home that has been standing
 * longest is the one the division most wants moved, so it leads. `moveIn` is the
 * feed's own move-in date; anything unparseable (a word like "Ready now", or a
 * blank) sorts last within its status rather than jumping the queue.
 */
const moveInTime = (r: HomesiteInfo): number => {
  const raw = String((r as { moveIn?: string }).moveIn || "").trim();
  if (!raw) return Number.MAX_SAFE_INTEGER;
  const t = Date.parse(raw);
  return isFinite(t) ? t : Number.MAX_SAFE_INTEGER;
};

/** stable completeness→status→age→photo ordering; the input array is not mutated */
export function orderHomesites<T extends HomesiteInfo>(rows: T[] | undefined | null): T[] {
  const list = Array.isArray(rows) ? rows.slice() : [];
  return list
    .map((r, i) => ({ r, i }))
    .sort((a, b) =>
      completeRank(a.r) - completeRank(b.r)
      || homeStatusRank(a.r.status) - homeStatusRank(b.r.status)
      || moveInTime(a.r) - moveInTime(b.r)
      || photoRank(a.r.img) - photoRank(b.r.img)
      || a.i - b.i)
    .map((x) => x.r);
}
